import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuftragCtx } from '../pages/AuftragLayout.jsx';
import { Abschnittstitel, DokumentListe, ExcelKnopf, TitelFeld, useOnline } from '../pages/Checkliste.jsx';
import { useExcelDokumente, useMe } from '../hooks/data.js';
import { useAbschnitte, useExcelEntwurf } from '../hooks/useExcelEntwurf.js';
import { AbschnittsLeiste, SpeichernLeiste } from '../components/AbschnittsLeiste.jsx';
import { Icon } from '../components/Icon.jsx';
import { Stand } from '../components/Stand.jsx';
import { isoDate } from '../lib/format.js';
import { load } from '../lib/storage.js';
import { leeresFormular, neueZeile, standardsSetzen } from '../../api/src/lib/formulare/normalisieren.js';
import { Feld, Haken, Kurzinfo, gemerkt } from './Felder.jsx';
import { UebernehmenSheet } from './Uebernehmen.jsx';
import { FelderRaster, PruefSchritt, SchrittLeiste, TabellenSchritt, offenIn, schritteVon } from './Schritte.jsx';
import { useToast } from '../state/toast.jsx';
import { alleAktion, aufloesen, fehlendePflicht, fortschritt, istHaken, kontext, kurzinfo, setzen, zeigtFeld, uebernehmen, zeileEntfernen, zeileFertig, zeileKopieren, zeilenAktion, zeilenAnlegen } from './logik.js';

/**
 * Wo die Formulare in der App und in der API liegen. Die Übersichtsliste nutzt ihre bisherigen Routen, Seitenpfade und
 * Entwürfe weiter (gleiche Dateien, gleiche Daten) – so funktionieren Links, Kacheln und angefangene Entwürfe weiter.
 */
export const ORTE = { uebersichtsliste: { art: 'checklisten', pfad: 'checkliste', entwurf: 'checkliste' } };
export const ortVon = (v) => ORTE[v.id] ?? { art: `formulare/${v.id}`, pfad: `formulare/${v.id}`, entwurf: `formular.${v.id}` };

/** Alle Dokumente einer Vorlage im Auftrag – öffnen oder neu anlegen. */
export function FormularListe({ vorlage: v }) {
  const { nr } = useAuftragCtx();
  const ort = ortVon(v);
  return <DokumentListe q={useExcelDokumente(ort.art, nr)} pfad={ort.pfad} entwurfPrefix={ort.entwurf} leer={`Für diesen Auftrag gibt es noch keine ${v.mehrzahl}.`}
    neu={`Neue ${v.titel}`} hinweis={v.hinweis} icon={v.icon} />;
}

/** Ein Dokument bearbeiten – Kopf, Abschnitte, Speichern-Leiste; alles aus der Vorlage. */
export function FormularEditor({ vorlage: v, dateiId }) {
  const { nr, base, auftrag } = useAuftragCtx();
  const me = useMe();
  const online = useOnline();
  const ort = ortVon(v);
  const heute = isoDate();
  const location = useLocation();
  const schrittRef = useRef(0);
  const { neu, q, server, daten, entwurf, veraltet, team, aendern, verwerfen, absenden, fehler, speichert, vorgemerkt } = useExcelEntwurf({
    art: ort.art, nr, dateiId, base, pfad: ort.pfad, entwurfPrefix: ort.entwurf,
    leer: (liste) => {
      const d = standardsSetzen(v, leeresFormular(v));
      const ctx = kontext(v, d, { heute, meinName: me.data?.name ?? '', team: liste.team ?? [] });
      for (const f of [...(v.kopf ?? []), ...(v.fuss ?? [])]) {
        if (f.vorbelegung === 'auftrag.baustelle') d[f.id] = liste.baustelle ?? '';
        else if (f.vorbelegung === 'auftrag.titel') d[f.id] = auftrag?.titel ?? '';
        else if (f.vorbelegung === 'auftrag.kunde') d[f.id] = auftrag?.kunde?.firma ?? '';
        else if (f.vorbelegung) d[f.id] = aufloesen(f.vorbelegung, { ctx });
      }
      return d;
    },
    titelFehlt: v.titelFeld.fehlt,
    navState: () => ({ schritt: schrittRef.current }), // neues Formular gespeichert: im gleichen Schritt bleiben
    gespeichert: (d) => `«${d.titel}» gespeichert – Excel liegt in den Dateien${v.ablage === 'kunde' ? ' des Kunden' : ''}`,
  });
  const abschnitte = useAbschnitte(v.abschnitte.map((a) => a.id));
  const toast = useToast();
  const [uebernahme, setUebernahme] = useState(false);
  const schrittKey = `bs.schritt.${ort.entwurf}.${nr}.${dateiId}`;
  // Schritt merken, solange ein Entwurf da ist (Neuladen, Funkloch); sonst beginnt man vorne
  const [schritt, setSchrittRoh] = useState(() => { try { return load(`${ort.entwurf}.${nr}.${dateiId}`, null) ? Number(sessionStorage.getItem(schrittKey)) || 0 : (location.state?.schritt ?? 0); } catch { return 0; } });
  schrittRef.current = schritt;
  const setSchritt = (i) => { setSchrittRoh(i); try { sessionStorage.setItem(schrittKey, String(i)); } catch { /* egal */ } };
  useEffect(() => { document.querySelector('.scroll')?.scrollTo?.({ top: 0 }); }, [schritt]);

  if (q.isLoading && !daten) return <div className="narrow"><div className="skeleton" style={{ height: 120 }} /><div className="skeleton" style={{ height: 320 }} /></div>;
  if (!daten) {
    return (
      <div className="narrow">
        <div className="empty" role="alert">
          {q.error?.message ?? `${v.titel} nicht verfügbar.`}
          <button className="btn" onClick={() => q.refetch()}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  const tun = (fn) => aendern((d) => fn(d, kontext(v, d, { heute, meinName: me.data?.name ?? '', team, gemerkt })));
  const kopfNurLesen = (v.kopf ?? []).filter((f) => f.nurLesen);
  const kopfFelder = (v.kopf ?? []).filter((f) => !f.nurLesen);
  const datei = server?.datei;
  const fehlend = fehlendePflicht(v, daten);
  const setzeFeld = (f) => (w) => aendern((d) => ({ ...d, [f.id]: typeof w === 'function' ? w(d[f.id]) : w }));
  const uebernommen = (quelle, titel) => {
    const vorher = structuredClone(daten);
    aendern((d) => uebernehmen(v, d, quelle));
    setUebernahme(false);
    const n = Object.keys(v.uebernehmen.tabellen ?? {}).map((aid) => quelle[aid]?.length ?? 0).reduce((x, y) => x + y, 0);
    toast({ text: `Übernommen aus «${titel}»${n ? `: ${n} ${n === 1 ? 'Zeile' : 'Zeilen'}` : ''} – Messwerte und Prüfung neu erfassen`, undo: () => aendern(() => vorher), long: true });
  };

  const kopf = (
    <section className="card-dark" style={{ padding: 18, gap: 8 }}>
      <span className="eyebrow on-dark-muted">{v.titel}</span>
      <TitelFeld wert={daten.titel ?? ''} onChange={(titel) => aendern((d) => ({ ...d, titel }))} placeholder={v.titelFeld.platzhalter} max={v.titelFeld.max} autoFocus={neu && !daten.titel} label={v.titelFeld.label ?? `Titel: ${v.titel}`} groesse={24} />
      {kopfNurLesen.map((f) => <span key={f.id} className="on-dark-muted" style={{ fontSize: 13, lineHeight: 1.4 }}>{daten[f.id] || '–'}</span>)}
      {datei && <ExcelKnopf datei={datei} />}
    </section>
  );

  if (v.schritte) {
    const schritte = schritteVon(v);
    const i = Math.min(schritt, schritte.length - 1);
    const s = schritte[i];
    const offen = schritte.map((x, n) => ({ n, titel: x.titel, liste: offenIn(v, x, daten) })).filter((x) => x.liste.length);
    return (
      <div className="narrow schrittansicht">
        <div className="schrittkopf">
          <Link to={`${base}/${ort.pfad}`} className="linkbtn" aria-label={`Alle ${v.mehrzahl}`}>‹ Zur Liste</Link>
          <Stand abfragen={[q]} />
        </div>
        <SchrittLeiste schritte={schritte} aktiv={i} daten={daten} v={v} onWahl={setSchritt} />
        {veraltet && (
          <div className="notice err" role="alert">
            <Icon name="warn" size={18} />
            <span>{v.titel} wurde inzwischen geändert (in der App oder in Excel). Deine Eingaben sind noch hier, lassen sich aber nicht speichern.
              <button className="linkbtn" onClick={verwerfen} style={{ marginLeft: 6 }}>Aktuellen Stand laden</button></span>
          </div>
        )}

        {s.key === 'kopf' && (
          <>
            {kopf}
            {v.uebernehmen && (
              <button type="button" className="tile-knopf" onClick={() => setUebernahme(true)} aria-label={v.uebernehmen.titel}>
                <Icon name="upload" size={20} />
                <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 800, fontSize: 15 }}>{v.uebernehmen.titel}</span>
                  <span className="sub ellipsis" style={{ fontSize: 13 }}>Anlage und Stromkreise – nur noch messen</span>
                </span>
                <Icon name="chevron" size={18} />
              </button>
            )}
            {kopfFelder.length > 0 && <section className="card"><FelderRaster felder={kopfFelder} daten={daten} team={team} setzeFeld={setzeFeld} /></section>}
          </>
        )}
        {s.a?.art === 'festeZeilen' && <PruefSchritt a={s.a} daten={daten} team={team} tun={tun} />}
        {s.a?.art === 'tabelle' && <TabellenSchritt a={s.a} daten={daten} team={team} tun={tun} />}
        {s.key === 'fuss' && (
          <>
            <div className="schritttitel"><h2 className="h1">{s.titel}</h2><span className="sub">Bemerkungen, Fotos und was noch fehlt</span></div>
            <section className="card"><FelderRaster felder={v.fuss} daten={daten} team={team} setzeFeld={setzeFeld} /></section>
            {offen.length > 0 ? (
              <section className="card stack" style={{ gap: 4 }} aria-label="Noch offen">
                <h3 className="h2" style={{ fontSize: 17 }}>Noch offen</h3>
                <span className="sub" style={{ fontSize: 13 }}>Speichern geht trotzdem – ergänzen kannst du später.</span>
                {offen.map((o) => (
                  <button key={o.n} type="button" className="listrow plainrow" onClick={() => setSchritt(o.n)} style={{ cursor: 'pointer' }}>
                    <span className="fehltpunkt">{o.liste.length}</span>
                    <span className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 800 }}>{o.titel}</span>
                      <span className="sub ellipsis" style={{ fontSize: 12 }}>{o.liste.join(', ')}</span>
                    </span>
                    <Icon name="chevron" size={18} />
                  </button>
                ))}
              </section>
            ) : (
              <div className="notice ok" role="status"><Icon name="check" size={18} />Alles ausgefüllt.</div>
            )}
          </>
        )}
        {uebernahme && <UebernehmenSheet v={v} art={ort.art} nr={nr} dateiId={dateiId} onWahl={uebernommen} onClose={() => setUebernahme(false)} />}

        <SpeichernLeiste entwurf={entwurf} vorgemerkt={vorgemerkt} fehler={fehler} online={online} speichert={speichert} veraltet={veraltet} titelOk={!!daten.titel?.trim()}
          onVerwerfen={verwerfen} onSpeichern={absenden} label={`${v.titel} speichern`}
          weiter={i < schritte.length - 1 ? { titel: schritte[i + 1].titel, onClick: () => setSchritt(i + 1) } : null}
          zurueck={i > 0 ? () => setSchritt(i - 1) : null} />
      </div>
    );
  }

  return (
    <div className="narrow">
      <div className="between" style={{ gap: 8 }}>
        <Link to={`${base}/${ort.pfad}`} className="linkbtn" style={{ fontSize: 13, minHeight: 48, display: 'inline-flex', alignItems: 'center' }}>‹ Alle {v.mehrzahl}</Link>
        <Stand abfragen={[q]} />
      </div>
      <section className="card-dark" style={{ padding: 18, gap: 8 }}>
        <span className="eyebrow on-dark-muted">{v.titel}</span>
        <TitelFeld wert={daten.titel ?? ''} onChange={(titel) => aendern((d) => ({ ...d, titel }))} placeholder={v.titelFeld.platzhalter} max={v.titelFeld.max} autoFocus={neu} label={v.titelFeld.label ?? `Titel: ${v.titel}`} groesse={24} />
        {kopfNurLesen.map((f) => <span key={f.id} className="on-dark-muted" style={{ fontSize: 13, lineHeight: 1.4 }}>{daten[f.id] || '–'}</span>)}
        {datei && <ExcelKnopf datei={datei} />}
      </section>

      {v.uebernehmen && (
        <button type="button" className="btn secondary block" onClick={() => setUebernahme(true)}>
          <Icon name="upload" size={18} />{v.uebernehmen.titel}
        </button>
      )}
      {uebernahme && <UebernehmenSheet v={v} art={ort.art} nr={nr} dateiId={dateiId} onWahl={uebernommen} onClose={() => setUebernahme(false)} />}

      {kopfFelder.length > 0 && (
        <section className="card stack" style={{ gap: 12 }}>
          {kopfFelder.map((f) => <Feld key={f.id} f={f} wert={daten[f.id]} team={team} onChange={setzeFeld(f)} />)}
        </section>
      )}

      {v.abschnitte.length > 1 && (
        <AbschnittsLeiste aktiv={abschnitte.aktiv} onSpringe={abschnitte.springe} eintraege={v.abschnitte.map((a) => {
          const p = fortschritt(a, daten);
          return { key: a.id, titel: a.titel, info: a.art === 'tabelle' && !p.gesamt ? 'anlegen' : fortschrittText(a, p, '/') };
        })} />
      )}

      {veraltet && (
        <div className="notice err" role="alert">
          <Icon name="warn" size={18} />
          <span>{v.titel} wurde inzwischen geändert (in der App oder in Excel). Deine Eingaben sind noch hier, lassen sich aber nicht speichern.
            <button className="linkbtn" onClick={verwerfen} style={{ marginLeft: 6 }}>Aktuellen Stand laden</button></span>
        </div>
      )}

      {v.abschnitte.map((a) => (
        <div key={a.id} className="stack" style={{ gap: 10 }}>
          <div ref={abschnitte.ref(a.id)} />
          {a.art === 'tabelle'
            ? <Tabelle a={a} daten={daten} team={team} tun={tun} />
            : <FesteZeilen a={a} daten={daten} team={team} tun={tun} />}
        </div>
      ))}

      {v.fuss?.length > 0 && (
        <section className="card stack" style={{ gap: 12 }}>
          <h2 className="h2">{v.fussTitel ?? 'Abschluss'}</h2>
          {v.fuss.map((f) => <Feld key={f.id} f={f} wert={daten[f.id]} team={team} onChange={setzeFeld(f)} />)}
        </section>
      )}

      {fehlend.length > 0 && (entwurf || neu) && (
        <div className="notice info" role="status">
          <Icon name="list" size={18} />
          <span>Noch offen: {fehlend.slice(0, 6).join(', ')}{fehlend.length > 6 ? ` und ${fehlend.length - 6} weitere` : ''}. Speichern geht trotzdem – ergänzen kannst du später.</span>
        </div>
      )}

      <SpeichernLeiste entwurf={entwurf} vorgemerkt={vorgemerkt} fehler={fehler} online={online} speichert={speichert} veraltet={veraltet} titelOk={!!daten.titel?.trim()}
        onVerwerfen={verwerfen} onSpeichern={absenden} label={`${v.titel} speichern`} />
    </div>
  );
}

/** «3/9 getestet» bzw. ohne Fortschrittsfeld die Anzahl («3 Stromkreise»). */
const fortschrittText = (a, p, trenner) => (a.fortschritt?.feld ? `${p.n}${trenner}${p.gesamt} ${a.fortschritt.text ?? ''}`.trim() : `${p.gesamt} ${a.anzahl ?? a.titel}`);

/** Felder, die im zweispaltigen Raster die ganze Breite brauchen. */
const BREIT = new Set(['auswahl', 'mehrfach', 'text', 'person', 'unterschrift', 'fotos']);

// ─── Tabelle (Zeilen anlegen, z. B. Zonen) ────────────────────────────────────

function Tabelle({ a, daten, team, tun }) {
  const [anzahl, setAnzahl] = useState('');
  const zeilen = daten[a.id];
  const p = fortschritt(a, daten);
  const anlegen = (n) => tun((d) => zeilenAnlegen(a, d, n, () => neueZeile(a)));
  // Vorschläge je Auswahl: in diesem Formular verwendet, zuletzt verwendet (Gerät), Standard
  const vorschlaege = (f) => [...new Set([...zeilen.map((z) => z[f.id]).filter(Boolean), ...gemerkt(f), ...(f.optionen ?? [])])].slice(0, 8);

  if (!zeilen.length && a.anlegen) {
    return (
      <section className="card">
        <h2 className="h2">{a.anlegen.frage}</h2>
        <div className="wrapchips">
          {a.anlegen.vorschlaege.map((n) => <button key={n} type="button" className="chip tn" style={{ minWidth: 52 }} onClick={() => anlegen(n)}>{n}</button>)}
        </div>
        <div className="row" style={{ gap: 8 }}>
          <input className="input" type="number" inputMode="numeric" min={1} max={a.max} value={anzahl} onChange={(e) => setAnzahl(e.target.value)} placeholder="andere Anzahl" style={{ maxWidth: 160 }} aria-label={`Anzahl ${a.titel}`} />
          <button type="button" className="btn secondary" onClick={() => anlegen(Number(anzahl) || 1)} disabled={!Number(anzahl)}>Anlegen</button>
        </div>
        <span className="sub" style={{ fontSize: 12 }}>Weitere lassen sich jederzeit dazunehmen.</span>
      </section>
    );
  }
  return (
    <section className="stack" style={{ gap: 10 }}>
      <Abschnittstitel titel={a.titel} info={fortschrittText(a, p, ' von ')} />
      {zeilen.map((z, i) => (
        <TabellenZeile key={z.nr} a={a} z={z} team={team} vorschlaege={vorschlaege}
          onSetzen={(feld, wert) => tun((d, c) => setzen(null, d, a, i, feld, wert, c))}
          onAktion={() => tun((d, c) => zeilenAktion(null, d, a, i, c))}
          onKopieren={a.kopieren ? () => tun((d) => zeileKopieren(a, d, i)) : null}
          onEntfernen={a.einzelnEntfernen ? () => tun((d) => zeileEntfernen(a, d, i)) : null} />
      ))}
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn secondary" style={{ flex: 1 }} onClick={() => anlegen(zeilen.length + 1)} disabled={zeilen.length >= a.max}>
          <Icon name="plus" size={18} stroke={2.4} />{a.zeile} {zeilen.length + 1}
        </button>
        {zeilen.length > 0 && (
          <button type="button" className="btn secondary" onClick={() => tun((d) => { d[a.id].pop(); return d; })} aria-label={`${a.zeile} ${zeilen.length} entfernen`} title={`Letzte ${a.zeile} entfernen`}>
            <Icon name="minus" size={18} stroke={2.4} />
          </button>
        )}
      </div>
    </section>
  );
}

function TabellenZeile({ a, z, team, vorschlaege, onSetzen, onAktion, onKopieren, onEntfernen }) {
  const [offen, setOffen] = useState(false);
  const kopfFeld = a.spalten.find((f) => f.ort === 'kopf');
  const haken = a.spalten.filter((f) => istHaken(f) && f.ort !== 'details');
  const details = a.spalten.filter((f) => f !== kopfFeld && !haken.includes(f));
  const fertig = zeileFertig(a, z);
  const name = `${a.zeile} ${z.nr}`;
  return (
    <div className="card" style={{ gap: 10, padding: 14, borderLeft: `4px solid ${fertig ? 'var(--accent)' : 'var(--line-2)'}` }}>
      <div className="row" style={{ gap: 8 }}>
        <span aria-label={name} style={{ flexShrink: 0, width: 44, height: 48, borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          background: fertig ? 'var(--accent)' : 'var(--bg)', color: fertig ? '#fff' : 'var(--ink)' }}>
          <span style={{ fontSize: 9, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase' }}>{a.zeile}</span>
          <span style={{ fontSize: 17, fontWeight: 800 }}>{z.nr}</span>
        </span>
        {kopfFeld && (
          <input className="input" style={{ height: 48, flex: 1, minWidth: 0 }} value={z[kopfFeld.id]} onChange={(e) => onSetzen(kopfFeld.id, e.target.value)}
            placeholder={kopfFeld.platzhalter} maxLength={kopfFeld.max} aria-label={`${kopfFeld.titel} ${name}`} />
        )}
        {a.aktion && (
          <button type="button" className={`btn${fertig ? ' secondary' : ''}`} style={{ minHeight: 48, padding: '0 12px', whiteSpace: 'nowrap' }} onClick={onAktion}
            aria-label={fertig ? `${name} zurücksetzen` : `${name} komplett erledigt`}>
            {fertig ? (a.aktion.zurueck ?? 'Zurück') : a.aktion.titel}
          </button>
        )}
      </div>
      {haken.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(haken.length, 4)}, minmax(0, 1fr))`, gap: 6 }}>
          {haken.map((f) => <Haken key={f.id} breit an={z[f.id] === true} label={f.titel} onClick={() => onSetzen(f.id, !z[f.id])} />)}
        </div>
      )}
      {details.length > 0 && (
        <>
          <Kurzinfo text={kurzinfo(a, z)} offen={offen} onClick={() => setOffen((x) => !x)} leer={a.detailsLeer ?? 'Details'} />
          {offen && (
            <div style={a.detailsRaster === 2 ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 } : { display: 'flex', flexDirection: 'column', gap: 10 }}>
              {details.map((f) => (
                <div key={f.id} style={a.detailsRaster === 2 && BREIT.has(f.typ) ? { gridColumn: '1 / -1' } : undefined}>
                  <Feld f={f} wert={z[f.id]} team={team} vorschlaege={vorschlaege(f)} aria={`${f.titel} ${name}`} onChange={(w) => onSetzen(f.id, w)} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {(onKopieren || onEntfernen) && (offen || !details.length) && (
        <div className="row" style={{ gap: 8 }}>
          {onKopieren && <button type="button" className="btn secondary" style={{ flex: 1, minHeight: 48 }} onClick={onKopieren}><Icon name="plus" size={16} stroke={2.4} />Kopieren</button>}
          {onEntfernen && <button type="button" className="btn secondary" style={{ minHeight: 48 }} onClick={onEntfernen} aria-label={`${name} entfernen`}><Icon name="trash" size={16} />Entfernen</button>}
        </div>
      )}
    </div>
  );
}

// ─── Feste Zeilen (z. B. Anlagenteile) ────────────────────────────────────────

function FesteZeilen({ a, daten, team, tun }) {
  const p = fortschritt(a, daten);
  return (
    <>
      <Abschnittstitel titel={a.titel} info={fortschrittText(a, p, ' von ')}
        aktion={a.aktion?.fuerAlle && <button type="button" className="btn secondary" style={{ minHeight: 48, padding: '0 12px', fontSize: 13 }} onClick={() => tun((d, c) => alleAktion(null, d, a, c))}>{a.aktion.titel}</button>} />
      <section className="card" style={{ gap: 0, paddingTop: 4, paddingBottom: 6 }}>
        {a.zeilen.map((zd) => (
          <FesteZeile key={zd.id} a={a} zd={zd} z={daten[a.id][zd.id]} team={team} onSetzen={(feld, wert) => tun((d, c) => setzen(null, d, a, zd.id, feld, wert, c))} />
        ))}
      </section>
    </>
  );
}

function FesteZeile({ a, zd, z, team, onSetzen }) {
  const [offen, setOffen] = useState(false);
  const inZeile = a.spalten.filter((f) => f.ort === 'zeile');
  const details = a.spalten.filter((f) => f.ort !== 'zeile' && zeigtFeld(a, f, zd, z));
  const kurz = kurzinfo(a, z);
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--line-2)' }}>
      <div className="row" style={{ gap: 6, alignItems: 'center' }}>
        <button type="button" onClick={() => setOffen((x) => !x)} aria-expanded={offen} disabled={!details.length}
          style={{ all: 'unset', cursor: details.length ? 'pointer' : 'default', flex: 1, minWidth: 0, minHeight: 48, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: 14, lineHeight: 1.25 }}>
            {zd.titel}
            {details.length > 0 && <span style={{ display: 'inline-flex', transform: `rotate(${offen ? -90 : 90}deg)`, color: 'var(--muted)', flexShrink: 0 }}><Icon name="chevron" size={14} /></span>}
          </span>
          {kurz && <span className="ellipsis" style={{ fontSize: 12, color: 'var(--ink-2)' }}>{kurz}</span>}
        </button>
        {inZeile.map((f) => (istHaken(f)
          ? <Haken key={f.id} schmal an={z[f.id] === true} label={f.titel} onClick={() => onSetzen(f.id, !z[f.id])} />
          : null))}
      </div>
      {offen && (
        <div className="stack" style={{ gap: 10, paddingTop: 10 }}>
          {details.map((f) => <Feld key={f.id} f={f} wert={z[f.id]} team={team} aria={`${f.titel} ${zd.titel}`} onChange={(w) => onSetzen(f.id, w)} />)}
        </div>
      )}
    </div>
  );
}
